import React, { useState, useEffect, useContext, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { UserProvider, useUser } from "./contexts/UserContext";
import { Tooltip } from 'react-tooltip';
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load dei componenti per velocizzare il caricamento iniziale
const LoginPage = lazy(() => import("./components/LoginPage"));
const Ingredients = lazy(() => import("./components/Ingredients"));
const CaricaXML = lazy(() => import("./components/CaricaXML"));
const Recipes = lazy(() => import("./components/Recipes"));
const RicetteUnificate = lazy(() => import("./components/RicetteUnificate"));
const Users = lazy(() => import("./components/Users"));
const UsersTest = lazy(() => import("./components/UsersTest"));
const UserProfile = lazy(() => import("./components/UserProfile"));
const RecipeDetail = lazy(() => import("./components/RecipeDetail"));
const Menu = lazy(() => import("./components/Menu"));
const TracciabilitaRapida = lazy(() => import('./components/TracciabilitaRapida'));
const MenuPubblico = lazy(() => import('./components/MenuPubblico'));
const MenuControlMobile = lazy(() => import('./components/MenuControlMobile'));
const WhatsAppSetup = lazy(() => import("./components/WhatsAppSetup"));
const Tracciabilita = lazy(() => import("./components/Tracciabilita"));
const PiattoDettaglioPublico = lazy(() => import("./components/PiattoDettaglioPublico"));
const Spesa = lazy(() => import("./components/Spesa"));
const SettingsPage = lazy(() => import("./components/Settings"));
const UnitsTest = lazy(() => import("./components/UnitsTest"));
// HACCP components
const HaccpFrigoriferi = lazy(() => import("./components/HaccpFrigoriferi"));
const HaccpReport = lazy(() => import("./components/HaccpReport"));
const HaccpOliiEsausti = lazy(() => import("./components/HaccpOliiEsausti"));
const HaccpPulizie = lazy(() => import("./components/HaccpPulizie"));
const Haccp = lazy(() => import("./components/Haccp"));
// Staff component
const Staff = lazy(() => import("./components/Staff"));
// Overview F.C. component
const OverviewFC = lazy(() => import("./components/OverviewFC"));
const Fornitori = lazy(() => import("./components/Fornitori"));
const UnitaDiMisura = lazy(() => import("./components/UnitaDiMisura"));
const Location = lazy(() => import("./components/Location"));

// Componenti sempre caricati (necessari subito)
import Navbar from "./components/Navbar";
import WelcomeBar from "./components/WelcomeBar";
import { Toaster } from "./components/ui/toaster";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SidePanelProvider } from "./contexts/SidePanelContext";
import { X } from 'lucide-react';

// Loading spinner per lazy loading
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-gray-900">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-400 text-sm">Caricamento...</p>
    </div>
  </div>
);

function AppContent() {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { isReady: userContextReady } = useUser(); // NEW: Check if UserContext is ready
  const location = useLocation();
  const navigate = useNavigate(); // Add navigate hook
  
  // Loading screen during auth check OR UserContext initialization
  if (isLoading || !userContextReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">
            {isLoading ? 'Caricamento AD Food Cost...' : 'Inizializzazione utente...'}
          </p>
        </div>
      </div>
    );
  }
  
  // Allow public access without authentication
  const isPublicMenuRoute = location.pathname === '/menu-pubblico';
  const isPiattoPublicoRoute = location.pathname.startsWith('/tracciabilita/piatti/');
  
  // Show login page if not authenticated (except for public routes)
  if (!isAuthenticated && !isPublicMenuRoute && !isPiattoPublicoRoute) {
    // Salva il percorso richiesto per il redirect dopo il login
    // Non salvare se siamo già sulla root
    if (location.pathname !== '/' && location.pathname !== '/dashboard') {
      sessionStorage.setItem('redirect_after_login', location.pathname);
      console.log('🔄 App: Saved redirect path for after login:', location.pathname);
    }
    return <LoginPage />;
  }
  
  // For public routes, render without navbar/auth
  if (isPublicMenuRoute) {
    return <MenuPubblico />;
  }
  
  if (isPiattoPublicoRoute) {
    return <PiattoDettaglioPublico />;
  }

  // Otherwise render the full app with navbar/header
  return (
    <div className="App min-h-screen bg-background transition-colors duration-200">
      <div className="flex flex-col h-screen">
        <Navbar />
        <WelcomeBar />
        
        <div className="flex flex-1 pt-28">
          {/* Main Content */}
          <main className="w-full overflow-x-hidden transition-all duration-300">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Default redirect to Prodotti Food */}
                <Route path="/" element={<Navigate to="/prodotti" replace />} />
                
                <Route path="/dashboard" element={<Navigate to="/prodotti" replace />} />
                <Route path="/ingredients" element={<Navigate to="/prodotti" replace />} />
                
                {/* Route unica Prodotti - mostra tutte le sezioni */}
                <Route path="/prodotti" element={<Ingredients section="all" />} />
                
                {/* Redirect vecchie route prodotti alla nuova pagina unificata */}
                <Route path="/prodotti/food" element={<Navigate to="/prodotti" replace />} />
                <Route path="/prodotti/beverage" element={<Navigate to="/prodotti" replace />} />
                <Route path="/prodotti/non-food" element={<Navigate to="/prodotti" replace />} />
                <Route path="/prodotti/attrezzature" element={<Navigate to="/prodotti" replace />} />
                
                <Route path="/prodotti/carica-xml" element={<CaricaXML />} />
                <Route path="/test-units" element={<UnitsTest />} />
                
                {/* Overview F.C. - Pagina standalone */}
                <Route path="/overview" element={<OverviewFC />} />
                
                {/* Ricette routes - Unified component with tabs */}
                <Route path="/recipes" element={<Navigate to="/ricette" replace />} />
                <Route path="/ricette" element={<RicetteUnificate />} />
                <Route path="/ricette/:tab" element={<RicetteUnificate />} />
                <Route path="/recipes/:id" element={<RecipeDetail />} />
                {/* Tracciabilità routes - Unified component with tabs */}
                <Route path="/tracciabilita" element={<Tracciabilita />} />
                <Route path="/tracciabilita/:tab" element={<Tracciabilita />} />
                <Route path="/tracciabilita/rapida" element={<TracciabilitaRapida />} />
                {/* HACCP routes - Unified component with tabs */}
                <Route path="/haccp" element={<Haccp />} />
                <Route path="/haccp/:tab" element={<Haccp />} />
                <Route path="/haccp/report" element={<HaccpReport />} />
                <Route path="/staff" element={<Staff />} />
                <Route path="/staff/:tab" element={<Staff />} />
                <Route path="/utenti" element={<Users />} />
                <Route path="/utenti-test" element={<UsersTest />} />
                <Route path="/utente/:userId" element={<UserProfile />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/menu-ristorante" element={<Menu />} />
              <Route path="/menu-pubblico" element={<MenuPubblico />} />
              <Route path="/whatsapp-menu" element={<WhatsAppSetup />} />
              <Route path="/menu-control" element={<MenuControlMobile />} />
              <Route path="/spesa" element={<Spesa />} />
              <Route path="/fornitori" element={<Fornitori />} />
              <Route path="/unita-misura" element={<UnitaDiMisura />} />
              <Route path="/location" element={<Location />} />

              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
            </Suspense>
          </main>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <UserProvider>
          <ThemeProvider>
            <SidePanelProvider>
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
              <Toaster />
              
              {/* Global Tooltip System - Fondo scuro e scritta bianca leggera */}
              <Tooltip
                id="global-tooltip"
                place="top"
                effect="solid"
                delayShow={1000}
                delayHide={100}
                className="react-tooltip-container"
              />
            </SidePanelProvider>
          </ThemeProvider>
        </UserProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;